import { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import StopIcon from "@mui/icons-material/StopRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";

import Card3D from "./Card3D";
import { api, money, tone } from "../api";

interface PaperRow {
  rank: number;
  symbol: string;
  lot: number;
  trades: number;
  net: number;
  unrealised: number;
  win_rate: number;
  profit_factor: number;
  max_drawdown: number;
  last_price: number;
  open: {
    direction: string; option: string; strike: number; lots: number; qty: number;
    entry_px: number; live_px: number; stop_px: number;
    points: number; pct: number; unrealised: number; reason: string;
  } | null;
  backtest: { trades: number; net: number; win: number; pf: number };
}

export default function PaperTrade() {
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = () => void api.paperTrade().then(setD).catch(() => {});

  useEffect(() => {
    load();
    const id = window.setInterval(load, 15000);
    return () => window.clearInterval(id);
  }, []);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); load(); }
  };

  if (!d) {
    return (
      <Card3D intensity={1}>
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress size={22} thickness={5} />
        </Stack>
      </Card3D>
    );
  }

  const live = d.status === "running";
  const stale = d.last_scan
    ? Math.round((Date.now() / 1000 - d.last_scan) / 60)
    : null;

  return (
    <Card3D intensity={1}>
      <Box sx={{ p: 2.2 }}>
        <Stack direction="row" alignItems="center" gap={1.2} flexWrap="wrap" sx={{ mb: 1.4 }}>
          <Box>
            <Typography sx={{ fontSize: ".92rem", fontWeight: 800 }}>
              Live Paper Trade · Stock Trend Master
            </Typography>
            <Typography sx={{ fontSize: ".64rem", color: "var(--muted)", mt: 0.2 }}>
              Buys ATM stock <b>options</b> (CE/PE) on the seven names that were
              profitable in the backtest. Simulated fills — no orders are sent.
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" gap={0.7}
            sx={{
              ml: "auto", px: 1.4, py: 0.6, borderRadius: "20px",
              background: live
                ? "linear-gradient(145deg,#0f7a45,#064d29)"
                : "linear-gradient(145deg,#2a3242,#151b25)",
              boxShadow: live ? "0 4px 14px rgba(0,230,118,.28)" : "none",
            }}>
            <span className={live ? "dot-live" : "dot-off"} />
            <Typography sx={{ fontSize: ".7rem", fontWeight: 800, color: "#fff" }}>
              {live ? "LIVE" : d.status.toUpperCase()}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.7}
            onClick={() => !busy && void act(live ? api.paperTradeStop : api.paperTradeStart)}
            sx={{
              px: 1.8, py: 0.85, borderRadius: "10px", cursor: busy ? "wait" : "pointer",
              fontWeight: 800, fontSize: ".76rem", color: "#fff",
              background: live
                ? "linear-gradient(150deg,#b3261e,#7a1520)"
                : "linear-gradient(150deg,#2563eb,#6d3bf6)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,.35), 0 8px 20px rgba(0,0,0,.4)",
              transition: "transform 160ms cubic-bezier(.22,1,.36,1)",
              "&:hover": { transform: "translateY(-2px)" },
            }}>
            {live ? <StopIcon sx={{ fontSize: 17 }} /> : <PlayIcon sx={{ fontSize: 17 }} />}
            {live ? "Stop" : "Live"}
          </Stack>

          <Tooltip title="Force one scan now">
            <Box onClick={() => !busy && void act(api.paperTradeScan)}
              sx={{
                p: 0.85, borderRadius: "10px", cursor: busy ? "wait" : "pointer",
                border: "1px solid var(--border)", display: "flex",
                background: "linear-gradient(165deg,#141d29,#0a1017)",
                "&:hover": { borderColor: "var(--purple)" },
              }}>
              <RefreshIcon sx={{ fontSize: 17, color: "var(--muted)" }} />
            </Box>
          </Tooltip>
        </Stack>

        {!live && (
          <Typography sx={{
            fontSize: ".72rem", color: "var(--muted)", mb: 1.2, px: 1.2, py: 0.8,
            borderRadius: "8px", background: "#0e1622",
            border: "1px solid var(--border)",
          }}>
            Not running. Nothing starts on its own — press <b>Live</b> to begin
            scanning, <b>Stop</b> to halt it.
            {d.open_positions > 0 && (
              <>
                {" "}<b style={{ color: "var(--gold)" }}>
                  {d.open_positions} position{d.open_positions > 1 ? "s" : ""} from
                  an earlier session {d.open_positions > 1 ? "are" : "is"} still
                  open and will not be managed until you press Live.
                </b>
              </>
            )}
          </Typography>
        )}

        {d.needs_auth && (
          <Typography sx={{
            fontSize: ".72rem", color: "var(--red)", mb: 1.2, px: 1.2, py: 0.9,
            borderRadius: "8px", background: "rgba(244,67,54,.08)",
            border: "1px solid rgba(244,67,54,.3)",
          }}>
            <b>Dhan credentials need refreshing.</b> {d.error} Until then no
            quotes arrive and nothing can trade.
          </Typography>
        )}

        {!d.needs_auth && d.error && (
          <Typography sx={{ fontSize: ".72rem", color: "var(--red)", mb: 1.2 }}>
            {d.error}
          </Typography>
        )}

        {(d.notes ?? []).map((n: string) => (
          <Typography key={n} sx={{
            fontSize: ".72rem", color: "var(--gold)", mb: 1.2, px: 1.2, py: 0.8,
            borderRadius: "8px", background: "rgba(255,215,0,.07)",
            border: "1px solid rgba(255,215,0,.25)",
          }}>{n}</Typography>
        ))}

        <Stack direction="row" gap={2.5} flexWrap="wrap" sx={{ mb: 1.4 }}>
          <Stat label="Realised" value={money(d.net, true)} cls={tone(d.net)} />
          <Stat label="Unrealised" value={money(d.unrealised, true)} cls={tone(d.unrealised)} />
          <Stat label="Trades" value={String(d.trades)} />
          <Stat label="Open" value={String(d.open_positions)} />
          <Stat label="Scans" value={String(d.scans)} />
          <Stat label="Last scan"
                value={stale === null ? "—" : stale < 1 ? "just now" : `${stale}m ago`} />
        </Stack>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", minWidth: 1010, borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {["#", "Stock", "Lot", "Spot", "Position", "Entry ₹", "Live ₹", "± pts", "Unrealised",
                  "Trades", "Net P&L", "Win%", "PF", "Max DD", "vs Backtest"].map((h) => (
                  <Box key={h} component="th" sx={{
                    textAlign: ["#", "Stock", "Position"].includes(h) ? "left" : "right",
                    fontSize: ".55rem", fontWeight: 800, letterSpacing: ".06em",
                    textTransform: "uppercase", color: "var(--muted)",
                    py: 0.8, px: 0.7, borderBottom: "1px solid var(--border)",
                    whiteSpace: "nowrap",
                  }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {d.rows.map((r: PaperRow) => (
                <Box component="tr" key={r.symbol} sx={{ "&:hover": { background: "#0e1622" } }}>
                  <Box component="td" sx={{ ...cell, color: "var(--muted)" }}>{r.rank}</Box>
                  <Box component="td" sx={{ ...cell, fontWeight: 700 }}>{r.symbol}</Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right", color: "var(--muted)" }}>{r.lot}</Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.last_price ? r.last_price.toFixed(2) : "—"}
                  </Box>
                  <Box component="td" sx={{ ...cell, fontSize: ".68rem" }}>
                    {r.open ? (
                      <Tooltip title={`${r.open.reason} · ${r.open.qty} qty · stop ₹${r.open.stop_px}`}>
                        <Box component="span" sx={{
                          px: 0.7, py: 0.2, borderRadius: "5px", fontWeight: 800,
                          color: r.open.option === "CE" ? "var(--green)" : "var(--red)",
                          border: `1px solid ${r.open.option === "CE" ? "rgba(0,230,118,.35)" : "rgba(244,67,54,.35)"}`,
                        }}>
                          {r.open.strike} {r.open.option} ×{r.open.lots}
                        </Box>
                      </Tooltip>
                    ) : <span style={{ color: "var(--muted)" }}>flat</span>}
                  </Box>

                  {/* option premium: paid, now, and the move between them */}
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.open ? r.open.entry_px.toFixed(2) : "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right", fontWeight: 700 }}>
                    {r.open ? r.open.live_px.toFixed(2) : "—"}
                  </Box>
                  <Box component="td" className={`mono ${r.open ? tone(r.open.points) : ""}`}
                       sx={{ ...cell, textAlign: "right", fontWeight: 800 }}>
                    {r.open
                      ? `${r.open.points >= 0 ? "+" : ""}${r.open.points.toFixed(2)}`
                      : "—"}
                    {r.open && (
                      <span style={{ opacity: 0.6, fontSize: ".62rem", marginLeft: 4 }}>
                        {r.open.pct >= 0 ? "+" : ""}{r.open.pct}%
                      </span>
                    )}
                  </Box>
                  <Box component="td" className={`mono ${r.open ? tone(r.open.unrealised) : ""}`}
                       sx={{ ...cell, textAlign: "right", fontWeight: 800 }}>
                    {r.open ? money(r.open.unrealised, true) : "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>{r.trades}</Box>
                  <Box component="td" className={`mono ${tone(r.net)}`} sx={{ ...cell, textAlign: "right", fontWeight: 800 }}>
                    {r.trades ? money(r.net, true) : "—"}
                  </Box>
                  <Box component="td" className="mono" sx={{ ...cell, textAlign: "right" }}>
                    {r.trades ? r.win_rate : "—"}
                  </Box>
                  <Box component="td" className={`mono ${r.profit_factor >= 1.5 ? "up" : ""}`}
                       sx={{ ...cell, textAlign: "right", fontWeight: 800 }}>
                    {r.trades ? r.profit_factor : "—"}
                  </Box>
                  <Box component="td" className="mono down" sx={{ ...cell, textAlign: "right", fontSize: ".68rem" }}>
                    {r.trades ? money(r.max_drawdown, true) : "—"}
                  </Box>
                  <Box component="td" sx={{ ...cell, textAlign: "right", fontSize: ".62rem", color: "var(--muted)" }}>
                    {money(r.backtest.net, true)} · {r.backtest.trades}t · PF {r.backtest.pf}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {d.recent?.length > 0 && (
          <Box sx={{ mt: 1.6 }}>
            <Typography sx={{
              fontSize: ".58rem", fontWeight: 800, letterSpacing: ".07em",
              textTransform: "uppercase", color: "var(--muted)", mb: 0.7,
            }}>
              Recent fills
            </Typography>
            <Stack gap={0.5}>
              {d.recent.slice(0, 6).map((t: any, i: number) => (
                <Stack key={i} direction="row" gap={1} alignItems="baseline" sx={{ fontSize: ".68rem" }}>
                  <Box sx={{ width: 92, fontWeight: 700 }}>{t.symbol}</Box>
                  <Box sx={{
                    fontWeight: 800,
                    color: t.option === "CE" ? "var(--green)" : "var(--red)",
                  }}>
                    {t.strike} {t.option}
                  </Box>
                  <Box className="mono" sx={{ color: "var(--muted)" }}>
                    ×{t.qty} · {t.entry_px} → {t.exit_px}
                  </Box>
                  <Box className={`mono ${tone(t.exit_px - t.entry_px)}`} sx={{ fontWeight: 800 }}>
                    {t.exit_px - t.entry_px >= 0 ? "+" : ""}
                    {(t.exit_px - t.entry_px).toFixed(2)} pts
                  </Box>
                  <Box sx={{ color: "var(--muted)", fontSize: ".6rem" }}>{t.reason}</Box>
                  <Box className={`mono ${tone(t.pnl)}`} sx={{ ml: "auto", fontWeight: 800 }}>
                    {money(t.pnl, true)}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Typography sx={{ fontSize: ".62rem", color: "var(--gold)", mt: 1.6, lineHeight: 1.5 }}>
          Bars are built from one quote a minute, so their highs and lows are
          sampled rather than true extremes — an intrabar stop can be missed and
          live results will not match the backtest tick for tick. Option premiums
          are Black-Scholes estimates on a 20-day expiry, not live option quotes.
        </Typography>
      </Box>
    </Card3D>
  );
}

function Stat({ label, value, cls = "" }: { label: string; value: string; cls?: string }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: ".55rem", fontWeight: 800, letterSpacing: ".07em",
        textTransform: "uppercase", color: "var(--muted)",
      }}>{label}</Typography>
      <Typography className={`mono ${cls}`} sx={{ fontSize: ".95rem", fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

const cell = {
  py: 0.8, px: 0.7, borderBottom: "1px solid var(--border)",
  fontSize: ".74rem", whiteSpace: "nowrap" as const,
};
